import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { EmployeePayrollCalculationResultDto, PayrollRun } from '../types';
import { formatCompanyDate, formatCompanyDateTime } from './companyDateFormat';

function formatYmd(iso: string | undefined | null): string {
  return formatCompanyDate(iso) || (iso ? String(iso).slice(0, 10) : '');
}

function formatLineCategory(category: string): string {
  if (category === 'TerminationLeavePayout') return 'Termination leave (PD-017)';
  if (category === 'TimecardExtras') return 'Extras';
  if (category === 'SundayPremium') return 'Sunday premium';
  if (category === 'BankHolidayPay') return 'Bank holiday pay';
  if (category === 'MatrixRate') return 'Pay matrix (by schedule)';
  if (category === 'WeekSubtotal') return 'Week subtotal (incl. OT)';
  return category;
}

function escapeCsvCell(value: string | number): string {
  const s = String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface PayrollExportOptions {
  run: PayrollRun;
  employees: EmployeePayrollCalculationResultDto[];
  /** When fewer employees than full preview (UI filters). */
  isFilteredView: boolean;
  totalEmployeesInPreview: number;
  /** Optional filename fragment e.g. location name. */
  fileNameSuffix?: string;
}

function buildMetaLines(opts: PayrollExportOptions, generatedAt: string): string[] {
  const { run, isFilteredView, totalEmployeesInPreview } = opts;
  const lines = [
    `Payroll run #${run.id}`,
    `Period: ${formatYmd(run.periodStart)} to ${formatYmd(run.periodEnd)}`,
    `Status: ${run.status}`,
    `Records in export: ${opts.employees.length}${isFilteredView ? ` (filtered; ${totalEmployeesInPreview} in full run)` : ''}`,
    `Generated: ${generatedAt}`,
  ];
  if (run.notes) lines.push(`Notes: ${run.notes}`);
  return lines;
}

/** UTF-8 BOM + CSV for Excel compatibility. */
export function downloadPayrollRegisterCsv(opts: PayrollExportOptions): void {
  const generatedAt = new Date().toISOString();
  const meta = buildMetaLines(opts, generatedAt);
  const rows: string[][] = [];

  meta.forEach((m) => rows.push([m]));
  rows.push([]);
  rows.push(['Employee ID', 'Employee name', 'Gross total', 'Success', 'Warnings', 'Errors']);

  for (const emp of opts.employees) {
    rows.push([
      String(emp.employeeId),
      emp.employeeName,
      emp.grossTotal.toFixed(2),
      emp.success ? 'Yes' : 'No',
      (emp.warnings ?? []).join('; '),
      (emp.errors ?? []).join('; '),
    ]);
  }

  rows.push([]);
  rows.push(['Earnings detail']);
  rows.push([
    'Employee ID',
    'Employee name',
    'Category',
    'Date',
    'Hours',
    'Hourly rate',
    'Multiplier',
    'Amount',
    'Detail',
  ]);

  for (const emp of opts.employees) {
    for (const ln of emp.lines ?? []) {
      rows.push([
        String(emp.employeeId),
        emp.employeeName,
        formatLineCategory(ln.category),
        ln.date ? formatYmd(ln.date) : '',
        Number(ln.hours).toFixed(4),
        Number(ln.hourlyRateApplied).toFixed(4),
        String(ln.multiplier),
        Number(ln.amount).toFixed(2),
        (ln.detail ?? '').replace(/\r?\n/g, ' '),
      ]);
    }
  }

  const csv = rows.map((r) => r.map(escapeCsvCell).join(',')).join('\r\n');
  const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8' });
  triggerDownload(
    blob,
    `payroll-run-${opts.run.id}-register${opts.fileNameSuffix ? `-${opts.fileNameSuffix}` : ''}.csv`
  );
}

export function downloadPayrollRegisterExcel(opts: PayrollExportOptions): void {
  const generatedAt = new Date().toISOString();
  const wb = XLSX.utils.book_new();

  const summaryRows: (string | number)[][] = [
    ['Payroll gross register (preview)'],
    [],
    ...buildMetaLines(opts, generatedAt).map((m) => [m]),
    [],
    ['Employee ID', 'Employee name', 'Gross total (EUR)', 'Success', 'Warnings', 'Errors'],
  ];
  for (const emp of opts.employees) {
    summaryRows.push([
      emp.employeeId,
      emp.employeeName,
      emp.grossTotal,
      emp.success ? 'Yes' : 'No',
      (emp.warnings ?? []).join('; '),
      (emp.errors ?? []).join('; '),
    ]);
  }
  const ws1 = XLSX.utils.aoa_to_sheet(summaryRows);
  ws1['!cols'] = [{ wch: 12 }, { wch: 28 }, { wch: 14 }, { wch: 10 }, { wch: 40 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

  const detailRows: (string | number)[][] = [
    [
      'Employee ID',
      'Employee name',
      'Category',
      'Date',
      'Hours',
      'Hourly rate',
      'Multiplier',
      'Amount (EUR)',
      'Detail',
    ],
  ];
  for (const emp of opts.employees) {
    for (const ln of emp.lines ?? []) {
      detailRows.push([
        emp.employeeId,
        emp.employeeName,
        formatLineCategory(ln.category),
        ln.date ? formatYmd(ln.date) : '',
        ln.hours,
        ln.hourlyRateApplied,
        ln.multiplier,
        ln.amount,
        ln.detail ?? '',
      ]);
    }
  }
  const ws2 = XLSX.utils.aoa_to_sheet(detailRows);
  ws2['!cols'] = [
    { wch: 12 },
    { wch: 24 },
    { wch: 22 },
    { wch: 12 },
    { wch: 10 },
    { wch: 12 },
    { wch: 10 },
    { wch: 12 },
    { wch: 36 },
  ];
  XLSX.utils.book_append_sheet(wb, ws2, 'Earnings lines');

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  triggerDownload(
    new Blob([out], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    `payroll-run-${opts.run.id}-register.xlsx`
  );
}

function buildSummaryBody(employees: EmployeePayrollCalculationResultDto[]): string[][] {
  return employees.map((emp) => [
    String(emp.employeeId),
    emp.employeeName,
    `€${emp.grossTotal.toFixed(2)}`,
    emp.success ? 'OK' : 'Error',
    String((emp.warnings ?? []).length),
    String((emp.errors ?? []).length),
  ]);
}

function buildDetailBody(employees: EmployeePayrollCalculationResultDto[]): string[][] {
  const body: string[][] = [];
  for (const emp of employees) {
    const lines = emp.lines ?? [];
    if (lines.length === 0) {
      body.push([String(emp.employeeId), emp.employeeName, '—', '—', '—', '—', '—', '—', 'No lines']);
      continue;
    }
    for (const ln of lines) {
      body.push([
        String(emp.employeeId),
        emp.employeeName,
        formatLineCategory(ln.category),
        ln.date ? formatYmd(ln.date) : '—',
        Number(ln.hours).toFixed(2),
        Number(ln.hourlyRateApplied).toFixed(2),
        String(ln.multiplier),
        `€${Number(ln.amount).toFixed(2)}`,
        (ln.detail ?? '').slice(0, 80),
      ]);
    }
  }
  return body;
}

/** A4 payroll-style PDF: header, metadata, summary table, earnings detail. */
export function downloadPayrollRegisterPdf(opts: PayrollExportOptions): void {
  const generatedAt = formatCompanyDateTime(new Date());
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 14;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(17, 24, 39);
  doc.text('Payroll gross register', margin, 16);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(75, 85, 99);
  let y = 22;
  for (const line of buildMetaLines(opts, generatedAt)) {
    doc.text(line, margin, y);
    y += 4;
  }

  const startY = y + 2;
  autoTable(doc, {
    startY,
    head: [['Employee ID', 'Name', 'Gross', 'Calc.', 'Warn.', 'Err.']],
    body: buildSummaryBody(opts.employees),
    styles: {
      font: 'helvetica',
      fontSize: 8,
      cellPadding: 1.8,
      lineColor: [226, 232, 240],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [30, 41, 59],
      textColor: 255,
      fontStyle: 'bold',
      halign: 'left',
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 22 },
      1: { cellWidth: 52 },
      2: { halign: 'right', cellWidth: 28 },
      3: { halign: 'center', cellWidth: 18 },
      4: { halign: 'center', cellWidth: 14 },
      5: { halign: 'center', cellWidth: 14 },
    },
    margin: { left: margin, right: margin },
    tableLineColor: [226, 232, 240],
    tableLineWidth: 0.1,
  });

  const afterSummary = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? startY + 40;
  let detailStart = afterSummary + 8;

  if (detailStart > 250) {
    doc.addPage();
    detailStart = 16;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(17, 24, 39);
  doc.text('Earnings detail (by line)', margin, detailStart - 2);

  autoTable(doc, {
    startY: detailStart,
    head: [
      ['Emp. ID', 'Name', 'Category', 'Date', 'Hrs', 'Rate', 'Mult.', 'Amount', 'Detail'],
    ],
    body: buildDetailBody(opts.employees),
    styles: {
      font: 'helvetica',
      fontSize: 7,
      cellPadding: 1.2,
      lineColor: [226, 232, 240],
      overflow: 'linebreak',
      cellWidth: 'wrap',
    },
    headStyles: {
      fillColor: [51, 65, 85],
      textColor: 255,
      fontStyle: 'bold',
    },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    columnStyles: {
      0: { cellWidth: 16 },
      1: { cellWidth: 28 },
      2: { cellWidth: 32 },
      3: { cellWidth: 22 },
      4: { halign: 'right', cellWidth: 14 },
      5: { halign: 'right', cellWidth: 14 },
      6: { halign: 'center', cellWidth: 12 },
      7: { halign: 'right', cellWidth: 20 },
      8: { cellWidth: 36 },
    },
    margin: { left: margin, right: margin },
  });

  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'italic');
    doc.text(
      `Confidential — payroll use only | ${generatedAt} | Page ${i} of ${totalPages}`,
      pageW / 2,
      doc.internal.pageSize.getHeight() - 6,
      { align: 'center' }
    );
  }

  doc.save(`payroll-run-${opts.run.id}-register.pdf`);
}
